/** 
 * PROJECT-STEP-ERRORS
 * Display errors for one step
 */
import { faExclamationTriangle, faInfoCircle } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import ListItem from "@/components/list-item"
import Space from "@/components/space"
import ToggleButton from "@/components/toggle-button"
import { store } from "@/index"
import { withTranslation, WithTranslation } from "react-i18next"
import { connect } from "react-redux"
import { projectEdit, projectStatus, projectUpdate } from "@/redux/project.actions"
import { ProjectState, ProjectStep, StepAlert } from "@/redux/project.types"
import { STATUS_SAVED, STATUS_SAVING } from "@/redux/_status.types"
import renderBold from "@/utils/render-bold.utils"
import { TemplateIssueLines } from "@/widgets/template-issues-list.widget"
import { useNavigate } from "react-router-dom"
import { useContext } from "react"
import { ProjectStepContext } from "@/components/project-step-context"
import { goToAlertTarget } from "@/utils/alert-target.utils"

interface StateProps extends WithTranslation{
  project : ProjectState
}

interface OwnProps{
  alert : StepAlert
  step : ProjectStep
}

type Props = StateProps & OwnProps

function ProjectStepErrors(props:Props) {
  const { t } = props
  const navigate = useNavigate()
  const projectSteps = useContext(ProjectStepContext)

  //Go to the place where an error can be fixed (questionnaire editor on the topic, or its parameters)
  function goToTarget(target?: string) {
    goToAlertTarget(target, { step: props.step, navigate, openStep: projectSteps.openStep })
  }

  function goToError(message: string) {
    if (props.step === "template" && props.project.active.isLaunch) return
    goToTarget((props.alert.targets || {})[message])
  }

  //Questionnaire : the problems are displayed as in the editor (the problems of a question together)
  const issues = props.alert.issues || []
  const errorIssues = issues.filter((x) => x.severity === "error")
  const warningIssues = issues.filter((x) => x.severity === "warning" && !x.ignored)
  const ignoredIssues = issues.filter((x) => x.ignored)
  //The questionnaire cannot be edited once the test or the survey is started : no link to the editor then
  const canEdit = !(props.step === "template" && props.project.active.isLaunch)
  const goToIssue = canEdit ? (id: string) => goToTarget(issues.find((x) => x.id === id)?.target) : undefined

  //Detect if warning active
  function isWarning(): boolean{
    return props.alert.warnings.length > 0 && props.project.active.showWarnings
  }

  //Warnings ignored in the questionnaire editor : greyed, follow the visibility of the warnings of the step
  function isIgnoredShown(): boolean{
    return (props.alert.ignored || []).length > 0 && props.project.active.showWarnings && (props.alert.warnings.length === 0 || isWarningActive())
  }

  //Is there a box displayed above the greyed one (no space between them)
  function hasBoxAbove(): boolean{
    return props.alert.errors.length > 0 || isWarning()
  }

  //Detect if the warning is active
  function isWarningActive():boolean{
    return props.project.active.isStepWarningActive(props.step)
  }
  
  //Toogle warnings for the step only
  async function toogleWarning(){

    store.dispatch(projectEdit(
      "inactiveWarnings", 
      isWarningActive()
      ?
      props.project.active.inactiveWarnings.concat([props.step]) 
      :
      props.project.active.inactiveWarnings.filter(x => x !== props.step)
    ))

    //Save to the database
    setTimeout(() => {

      save()

      async function save(){
        store.dispatch(projectStatus(STATUS_SAVING))
        await store.dispatch(projectUpdate(props.project.active))
        store.dispatch(projectStatus(STATUS_SAVED))
      }
      
    }, 500)

  }

  return (
    <>
      { props.alert.errors.length > 0 &&
      <div className="setup-card-alert setup-card-alert-error">
        <ListItem>
          <div>
            <div className="flex items-center gap-2 text-base font-bold">
              <FontAwesomeIcon icon={faExclamationTriangle}/>
              {(issues.length > 0 ? errorIssues.length : props.alert.errors.length) + " " + t("utils_error", { s: (issues.length > 0 ? errorIssues.length : props.alert.errors.length) > 1 ? "s" : ""})}
            </div>
            {issues.length > 0 ?
            <div className="mt-2 text-sm"><TemplateIssueLines groupByKind issues={errorIssues} severity="error" onSelect={goToIssue}/></div>
            :
            <div className="mt-2 flex flex-col gap-1 pl-6 text-sm">
              { props.alert.errors.map((error:string, i:number)=>
              <div key={i}
                className={"flex items-start gap-2" + ((props.alert.targets || {})[t(error)] ? " cursor-pointer hover:underline" : "")}
                onClick={() => goToError(t(error))}>
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-current"/>
                <span>{renderBold(t(error))}</span>
              </div>
              )}
            </div>
            }
          </div>
        </ListItem>
      </div>
      }

      { isWarning() &&
      <div
        className={"setup-card-alert " + (isWarningActive() ? "setup-card-alert-warning" : "grey-t")}
        style={props.alert.errors.length > 0 ? { marginTop: 0 } : undefined}>
        <ListItem isWithoutBorder={props.alert.errors.length > 0}>
          <div>
            <div className="flex items-center gap-2 text-base font-bold">
              <FontAwesomeIcon icon={faExclamationTriangle}/>
              {(issues.length > 0 ? warningIssues.length : props.alert.warnings.length) + " " + t("utils_warning", { s: (issues.length > 0 ? warningIssues.length : props.alert.warnings.length) > 1 ? "s" : ""})}
            </div>

            { isWarningActive() && (issues.length > 0 ?
            <div className="mt-2 text-sm"><TemplateIssueLines groupByKind issues={warningIssues} severity="warning" onSelect={goToIssue}/></div>
            :
            <div className="mt-2 flex flex-col gap-1 pl-6 text-sm">
              { props.alert.warnings.map((warning:string, i:number)=>
              <div key={i}
                className={"flex items-start gap-2" + ((props.alert.targets || {})[t(warning)] ? " cursor-pointer hover:underline" : "")}
                onClick={() => goToError(t(warning))}>
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-current"/>
                <span>{renderBold(t(warning))}</span>
              </div>
              )}
            </div>
            )}

            <div className="flex"
              style={{ marginTop : 12 }}>
              <ToggleButton color="#F2D600" 
                isActive={isWarningActive()}
                onToogle={() => toogleWarning()}
              />
              <div className="grey-t"
                style={{ fontSize : 12, margin : "4px 8px" }}>
                {t("project_show_warnings_step")}
              </div>
              <Space/>
            </div>
          </div>
        </ListItem>
      </div>
      }

      { isIgnoredShown() &&
      <div className="setup-card-alert grey-t"
        style={{ backgroundColor : "#8c8c8c20", marginTop : hasBoxAbove() ? 0 : undefined }}>
        <ListItem isWithoutBorder={hasBoxAbove()}>
          <div>
            <div className="flex items-center gap-2 text-base font-bold">
              <FontAwesomeIcon icon={faInfoCircle}/>
              {props.alert.ignored.length + " " + t("utils_warning", { s: props.alert.ignored.length > 1 ? "s" : ""})}
              <span className="text-sm font-normal">{"(" + t("template_issue_ignored") + ")"}</span>
            </div>
            {issues.length > 0 ?
            <div className="mt-2 text-sm"><TemplateIssueLines issues={ignoredIssues} severity="error"/></div>
            :
            <div className="mt-2 flex flex-col gap-1 pl-6 text-sm">
              { props.alert.ignored.map((message:string, i:number)=>
              <div key={i} className="flex items-start gap-2">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-current"/>
                <span>{renderBold(t(message))}</span>
              </div>
              )}
            </div>
            }
          </div>
        </ListItem>
      </div>
      }
    </>
  )

}

const mapStateToProps = state => ({
  project : state.project
})

export default connect(mapStateToProps)(withTranslation()(ProjectStepErrors))